import {
  IconArrowLeft,
  IconArrowRight,
  IconMail,
  IconPhone,
  IconBrandTelegram,
  IconBrandVk,
  IconLink,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "motion/react";

import { useEffect, useState } from "react";
import { cn } from "../../lib/cn";

const parseSocials = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [raw];
  } catch {
    return [raw];
  }
};

const SocialIcon = ({ url }) => {
  if (url.includes("t.me") || url.toLowerCase().includes("telegram")) {
    return <IconBrandTelegram className="h-5 w-5" />;
  }
  if (url.includes("vk.com")) {
    return <IconBrandVk className="h-5 w-5" />;
  }
  return <IconLink className="h-5 w-5" />;
};

export const AnimatedTestimonials = ({ testimonials, autoplay = false }) => {
  const [active, setActive] = useState(0);

  const handleNext = () => {
    setActive((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const isActive = (index) => {
    return index === active;
  };

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(handleNext, 5000);
      return () => clearInterval(interval);
    }
  }, [autoplay]);

  const randomRotateY = () => {
    return Math.floor(Math.random() * 21) - 10;
  };

  const socials = parseSocials(testimonials[active]?.socialMedia);

  return (
    <div
      className="mx-auto max-w-sm px-4 py-20 font-sans antialiased md:max-w-4xl md:px-8 lg:px-12">
      <div className="relative grid grid-cols-1 gap-10 md:grid-cols-2">
        <div>
          <div className="relative h-80 w-full">
            <AnimatePresence>
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id ?? index}
                  initial={{
                    opacity: 0,
                    scale: 0.9,
                    z: -100,
                    rotate: randomRotateY(),
                  }}
                  animate={{
                    opacity: isActive(index) ? 1 : 0.7,
                    scale: isActive(index) ? 1 : 0.95,
                    z: isActive(index) ? 0 : -100,
                    rotate: isActive(index) ? 0 : randomRotateY(),
                    zIndex: isActive(index)
                      ? 40
                      : testimonials.length + 2 - index,
                    y: isActive(index) ? [0, -80, 0] : 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.9,
                    z: 100,
                    rotate: randomRotateY(),
                  }}
                  transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 origin-bottom">
                  <img
                    src={testimonial.src}
                    alt={testimonial.name}
                    width={500}
                    height={500}
                    draggable={false}
                    className="h-full w-full rounded-3xl object-cover object-center" />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
        <div className="flex flex-col justify-between py-4">
          <motion.div
            key={active}
            initial={{
              y: 20,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            exit={{
              y: -20,
              opacity: 0,
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}>
            <h3 className="text-2xl font-bold text-black dark:text-white">
              {testimonials[active].name}
            </h3>
            <p className="mt-1 text-sm font-medium text-neutral-500 dark:text-neutral-400">
              {testimonials[active].designation}
            </p>

            <div className="mt-6 space-y-3 text-base text-neutral-700 dark:text-neutral-300">
              {testimonials[active].email && (
                <a
                  href={`mailto:${testimonials[active].email}`}
                  className="flex items-center gap-3 transition-colors hover:text-black dark:hover:text-white">
                  <IconMail className="h-5 w-5 shrink-0 text-neutral-400" />
                  <span>{testimonials[active].email}</span>
                </a>
              )}
              {testimonials[active].phoneNumber && (
                <a
                  href={`tel:${testimonials[active].phoneNumber}`}
                  className="flex items-center gap-3 transition-colors hover:text-black dark:hover:text-white">
                  <IconPhone className="h-5 w-5 shrink-0 text-neutral-400" />
                  <span>{testimonials[active].phoneNumber}</span>
                </a>
              )}
              {socials.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
                  {socials.map((url, index) => (
                    <a
                      key={index}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-neutral-500 transition-colors hover:text-black dark:text-neutral-400 dark:hover:text-white">
                      <SocialIcon url={url} />
                      <span className="text-sm">{url}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
          <div className="flex gap-4 pt-12 md:pt-0">
            <button
              onClick={handlePrev}
              aria-label="Предыдущий контакт"
              className={cn(
                "group/button flex h-9 w-9 items-center justify-center rounded-full",
                "bg-[rgb(151,1,14)] text-white transition-colors hover:bg-[rgb(120,1,11)]"
              )}>
              <IconArrowLeft
                className="h-5 w-5 transition-transform duration-300 group-hover/button:rotate-12" />
            </button>
            <button
              onClick={handleNext}
              aria-label="Следующий контакт"
              className={cn(
                "group/button flex h-9 w-9 items-center justify-center rounded-full",
                "bg-[rgb(151,1,14)] text-white transition-colors hover:bg-[rgb(120,1,11)]"
              )}>
              <IconArrowRight
                className="h-5 w-5 transition-transform duration-300 group-hover/button:-rotate-12" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
